import { Alert, Box, FormControl, Grid, Typography } from '@mui/material';
import CustodianType from '../../../Models/Locations/custodianType';
import Phones from './Phones';
import PhoneType from '../../../Models/Locations/phoneType';
import Panel from '../../../components/UI/Panel';
import Emails from './Emails';
import CustodianFieldSearchInput from './CustodianFieldSearchInput';
import CheckboxInput from './UI/CheckboxInput';
import Portals from './Portals';
import PortalType from '../../../Models/Locations/portalType';
import CustodianAddress from './CustodianAddress';
import TimeZone from '../../../Models/Locations/TimeZone';
import State from '../../../Models/Locations/state';
import { useState } from 'react';
import ConfirmationDialog from '../../../components/UI/ConfirmationDialog';
import * as Styles from '../../../assets/styles/appStyles';
import { Link } from 'react-router-dom';
import useAuth from '../../../hooks/context/useAuth';
import { appActions } from '../../../settings/appConfig';
import WarningIcon from '@mui/icons-material/Warning';

export default function CustodianBasicDetails({
  formik,
  custodianTypes,
  phoneTypes,
  portalTypes,
  showPanel,
  isFacility,
  timeZones,
  states,
  setIsFacility,
  isCustodian,
  setIsCustodian,
  facilityAssociationCount,
  custodianAssociationCount
}: {
  formik: any;
  custodianTypes: CustodianType[];
  phoneTypes: PhoneType[];
  timeZones: TimeZone[];
  states: State[];
  portalTypes: PortalType[];
  showPanel: boolean;
  isFacility: boolean;
  setIsFacility: (val: boolean) => void;
  isCustodian: boolean;
  setIsCustodian: (val: boolean) => void;
  facilityAssociationCount?: number;
  custodianAssociationCount?: number;
}) {

  const { isAuthorized } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);

  const handleFacilityCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked && facilityAssociationCount) {
      setDialogType('facility');
      setOpenDialog(true);
    } else {
      setIsFacility(e.target.checked);
      formik.setFieldValue("isFacility", e.target.checked);
    }
  };

  const handleCustodianCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked && custodianAssociationCount) {
      setDialogType('custodian');
      setOpenDialog(true);
    } else {
      setIsCustodian(e.target.checked);
      formik.setFieldValue("isCustodian", e.target.checked);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setDialogType(null);
  };

  const generateDescription = (entityType: any, count: any) => {
    return (
      <>
        You cannot update the entity type because this {entityType} has
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={Styles.entityCountIndicator}>
            {count + " →"}
          </Box> associated entities.
        </Box>
        <br />
        {formik?.values?.isFacility && isAuthorized(appActions.editAssociations) ? (
          <span>
            If you wish to manage the associations, please navigate to{' '}
            <Link to={`/associations/edit/${formik?.values?.id}`} style={{ color: '#f5831f' }}>
              Manage Associations
            </Link>
          </span>
        ) : (
          (formik?.values?.isFacility && <>
            <br />
            <Alert severity="warning" icon={<WarningIcon fontSize="small" />} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Please note, you do not have the necessary permissions to manage associations.
              </Typography>
            </Alert>
          </>)
        )}
      </>
    );
  };


  const content = (
    <>
      <Grid spacing={2} mx={1} px={1} container>
        <Grid item xs={12} px={2}>
          <CustodianFieldSearchInput
            duplicateErrorMessage="The name you have entered is slightly similar to some of the name of existing custodians. This could indicates that the custodian you are trying to create may already exist."
            label="Name"
            searchPropertyName="name"
            name="name"
            placeholder="Entity Name"
            maxLength={120}
            value={formik.values.name}
            formErrorMessage={
              formik.touched.name && formik.errors.name
                ? formik.errors.name
                : ''
            }
            hasFormError={
              !!(formik.touched.name && formik.errors.name)
            }
            onBlur={() => {
              formik.setFieldTouched(`name`, true);
            }}
            onChange={(e: any) => {
              formik.setFieldValue(`name`, e.target.value);
            }}
            required={true}
            disabled={formik.values?.id ? true:false}
          />
        </Grid>

        <Grid item my={2} xs={4} px={2}>
          <FormControl component="fieldset" fullWidth error={Boolean(formik.errors.isFacility || formik.errors.isCustodian)}>
            <Typography sx={{ fontWeight: 450, fontSize: '17px' }}>
              Entity Type <span style={{ color: 'red' }}>*</span> :
            </Typography>

            <Box display="flex" alignItems="center" mt={1} px={3}>
              <CheckboxInput
                label="Facility"
                keyName="isFacility"
                checked={isFacility}
                onChange={(e: any) => {
                  handleFacilityCheckboxChange(e);
                }}
                onBlur={() => {
                  formik.setFieldTouched("isFacility", true);
                }}
              />

              <CheckboxInput
                label="Custodian"
                keyName="isCustodian"
                checked={isCustodian}
                onChange={(e: any) => {
                  handleCustodianCheckboxChange(e);
                }}
                onBlur={() => {
                  formik.setFieldTouched("isCustodian", true);
                }}
              />
            </Box>
            {formik.submitCount > 0 && !(formik.values.isFacility || formik.values.isCustodian) && (
              <Typography variant="caption" color="error" sx={{ px: 3 }}>
                Please select Facility or Custodian
              </Typography>
            )}
          </FormControl>
        </Grid>

      </Grid>
      <Grid spacing={2} my={2} mx={0.4} px={1} container>
        <CustodianAddress states={states} timeZones={timeZones} formik={formik} />
      </Grid>
      <Grid spacing={2} my={2} mx={1} px={1} container>
        <Phones phoneTypes={phoneTypes} formik={formik} />
      </Grid>

      <Grid spacing={2} my={2} mx={1} px={1} container>
        <Portals portalTypes={portalTypes} formik={formik} isCustodian={isCustodian} />
      </Grid>

      <Grid spacing={2} my={2} mx={1} px={1} container>
        <Emails formik={formik} />
      </Grid>
      <Grid spacing={2} mx={1} px={1} container></Grid>
    </>
  );
  return (
    <>
      {showPanel === true && (
        <Panel headerText="Basic Details">{content}</Panel>
      )}
      {!showPanel && content}
      {openDialog &&
        <ConfirmationDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          title='Warning'
          description={generateDescription(dialogType, dialogType === 'facility' ? facilityAssociationCount : custodianAssociationCount)}
          primaryButtonText='Ok'
          onPrimaryAction={() => handleDialogClose()}
        />
      }
    </>
  );
}

CustodianBasicDetails.defaultProps = {
  showPanel: true,
};
